import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { routeService } from '../../../services/routeService';
import './AdminRoutes.css';

const AdminRouteAdd = () => {
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    additionalInfo: [{ title: '', description: '' }],
    distance: 0,
    time: 0,
    steps: 0,
    points: [{
      name: '',
      description: '',
      coordinates: '',
      photos: []
    }],
    mainPhoto: null,
    mainPhotoPreview: null
  });
  
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [coordinateErrors, setCoordinateErrors] = useState({});
  
  // Состояния для временных фотографий точек
  const [tempPointPhotos, setTempPointPhotos] = useState({});

  // Обработчик основных полей
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Парсинг строки координат
  const parseCoordinates = (coordString) => {
    if (!coordString || typeof coordString !== 'string') return null;
    
    const parts = coordString.trim().split(',').map(part => part.trim());
    
    if (parts.length !== 2) return null;
    
    const lat = parseFloat(parts[0]);
    const lng = parseFloat(parts[1]);
    
    if (isNaN(lat) || isNaN(lng)) return null;
    if (lat < -90 || lat > 90) return null;
    if (lng < -180 || lng > 180) return null;
    
    return { latitude: lat, longitude: lng };
  };

  // Расчет данных маршрута по точкам
  const calculateRouteFromPoints = (points) => {
    if (points.length < 2) {
      setFormData(prev => ({
        ...prev,
        distance: 0,
        time: 0,
        steps: 0
      }));
      return;
    }

    setCalculating(true);

    let totalDistance = 0;
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];
      
      const R = 6371;
      const dLat = (p2.latitude - p1.latitude) * Math.PI / 180;
      const dLon = (p2.longitude - p1.longitude) * Math.PI / 180;
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(p1.latitude * Math.PI/180) * Math.cos(p2.latitude * Math.PI/180) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const distance = R * c;
      totalDistance += distance;
    }

    const timeHours = totalDistance / 5;
    const timeMinutes = Math.round(timeHours * 60);
    const calculatedSteps = Math.round((totalDistance * 1000) / 0.75);

    setFormData(prev => ({
      ...prev,
      distance: parseFloat(totalDistance.toFixed(2)),
      time: timeMinutes,
      steps: calculatedSteps
    }));
    
    setCalculating(false);
  };

  // Обработчик основного фото
  const handleMainPhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        mainPhoto: file,
        mainPhotoPreview: previewUrl
      }));
    }
  };

  // Удаление основного фото
  const removeMainPhoto = () => {
    if (formData.mainPhotoPreview) {
      URL.revokeObjectURL(formData.mainPhotoPreview);
    }
    setFormData(prev => ({
      ...prev,
      mainPhoto: null,
      mainPhotoPreview: null
    }));
  };

  // Изменение доп. информации
  const handleAdditionalInfoChange = (index, field, value) => {
    const updatedInfo = [...formData.additionalInfo];
    updatedInfo[index][field] = value;
    setFormData(prev => ({ ...prev, additionalInfo: updatedInfo }));
  };

  // Добавление блока доп. информации
  const addAdditionalInfo = () => {
    setFormData(prev => ({
      ...prev,
      additionalInfo: [...prev.additionalInfo, { title: '', description: '' }]
    }));
  };

  // Удаление блока доп. информации
  const removeAdditionalInfo = (index) => {
    if (formData.additionalInfo.length > 1) {
      const updatedInfo = formData.additionalInfo.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, additionalInfo: updatedInfo }));
    }
  };

  // ========== ФУНКЦИИ ДЛЯ РАБОТЫ С ТОЧКАМИ ==========
  
  // Обновление информации о точке
  const updatePointInfo = (index, field, value) => {
    const updatedPoints = [...formData.points];
    updatedPoints[index][field] = value;
    
    setFormData(prev => {
      // Если меняются координаты, нужно пересчитать маршрут после парсинга
      if (field === 'coordinates') {
        // Очищаем ошибку для этой точки
        setCoordinateErrors(prevErrors => ({
          ...prevErrors,
          [index]: null
        }));
        
        // Пробуем распарсить координаты для пересчета
        const parsed = parseCoordinates(value);
        if (parsed && updatedPoints[index]) {
          updatedPoints[index].latitude = parsed.latitude;
          updatedPoints[index].longitude = parsed.longitude;
        }
        
        setTimeout(() => {
          const pointsForCalc = updatedPoints.map(p => ({
            latitude: p.latitude,
            longitude: p.longitude
          }));
          calculateRouteFromPoints(pointsForCalc);
        }, 100);
      }
      
      return { ...prev, points: updatedPoints };
    });
  };

  // Добавление новой точки
  const addPoint = () => {
    setFormData(prev => {
      const newPoints = [
        ...prev.points,
        {
          name: '',
          description: '',
          coordinates: '',
          photos: []
        }
      ];
      return { ...prev, points: newPoints };
    });
  };

  // Удаление точки
  const removePoint = (index) => {
    if (formData.points.length > 1) {
      if (window.confirm('Удалить эту точку?')) {
        setFormData(prev => {
          const newPoints = prev.points.filter((_, i) => i !== index);
          // Пересчитываем маршрут
          setTimeout(() => {
            const pointsForCalc = newPoints.map(p => ({
              latitude: p.latitude,
              longitude: p.longitude
            }));
            calculateRouteFromPoints(pointsForCalc);
          }, 100);
          return { ...prev, points: newPoints };
        });
        
        setTempPointPhotos(prev => {
          const newState = { ...prev };
          delete newState[index];
          return newState;
        });
        
        setCoordinateErrors(prev => {
          const newState = { ...prev };
          delete newState[index];
          return newState;
        });
      }
    }
  };

  // Перемещение точки вверх
  const movePointUp = (index) => {
    if (index > 0) {
      const newPoints = [...formData.points];
      [newPoints[index - 1], newPoints[index]] = [newPoints[index], newPoints[index - 1]];
      
      setFormData(prev => ({ ...prev, points: newPoints }));
      
      setTimeout(() => {
        const pointsForCalc = newPoints.map(p => ({
          latitude: p.latitude,
          longitude: p.longitude
        }));
        calculateRouteFromPoints(pointsForCalc);
      }, 100);
    }
  };

  // Перемещение точки вниз
  const movePointDown = (index) => {
    if (index < formData.points.length - 1) {
      const newPoints = [...formData.points];
      [newPoints[index], newPoints[index + 1]] = [newPoints[index + 1], newPoints[index]];
      
      setFormData(prev => ({ ...prev, points: newPoints }));
      
      setTimeout(() => {
        const pointsForCalc = newPoints.map(p => ({
          latitude: p.latitude,
          longitude: p.longitude
        }));
        calculateRouteFromPoints(pointsForCalc);
      }, 100);
    }
  };

  // Очистка всех точек
  const clearAllPoints = () => {
    if (window.confirm('Очистить все точки?')) {
      setFormData(prev => {
        const newPoints = [{
          name: '',
          description: '',
          coordinates: '',
          photos: []
        }];
        setTimeout(() => calculateRouteFromPoints([]), 100);
        return { ...prev, points: newPoints };
      });
      setTempPointPhotos({});
      setCoordinateErrors({});
    }
  };

  // Ручное изменение характеристик маршрута
  const handleRouteDataChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Валидация всех координат точек
  const validateCoordinates = () => {
    const errors = {};
    let isValid = true;
    
    formData.points.forEach((point, index) => {
      if (!point.coordinates || point.coordinates.trim() === '') {
        errors[index] = 'Введите координаты';
        isValid = false;
      } else {
        const parsed = parseCoordinates(point.coordinates);
        if (!parsed) {
          errors[index] = 'Некорректный формат. Используйте формат: 55.751244, 37.618423';
          isValid = false;
        } else {
          // Обновляем latitude и longitude в точке для корректной отправки
          const updatedPoints = [...formData.points];
          updatedPoints[index].latitude = parsed.latitude;
          updatedPoints[index].longitude = parsed.longitude;
          setFormData(prev => ({ ...prev, points: updatedPoints }));
        }
      }
    });
    
    setCoordinateErrors(errors);
    return isValid;
  };

  // Работа с фотографиями точек
  const handlePointPhotoSelect = (pointIndex, files) => {
    const filesArray = Array.from(files);
    setTempPointPhotos(prev => ({
      ...prev,
      [pointIndex]: [...(prev[pointIndex] || []), ...filesArray]
    }));
  };

  const removePointTempPhoto = (pointIndex, photoIndex) => {
    setTempPointPhotos(prev => ({
      ...prev,
      [pointIndex]: prev[pointIndex].filter((_, i) => i !== photoIndex)
    }));
  };

  // Загрузка всех фотографий после создания маршрута
  const uploadAllPhotos = async (routeId, createdPoints) => {
    try {
      // 1. Загрузка основной фотографии маршрута
      if (formData.mainPhoto) {
        await routeService.uploadMainPhoto(routeId, formData.mainPhoto);
        console.log('Основная фотография загружена');
      }
      
      // 2. Загружаем фотографии для каждой точки
      for (let i = 0; i < formData.points.length; i++) {
        const pointFiles = tempPointPhotos[i] || [];
        if (pointFiles.length > 0 && createdPoints && createdPoints[i]) {
          const pointId = createdPoints[i].id;
          await routeService.uploadPointPhotos(pointId, pointFiles);
          console.log(`Для точки ${i} загружено фотографий:`, pointFiles.length);
        }
      }
    } catch (error) {
      console.error('Ошибка при загрузке фотографий:', error);
      throw error;
    }
  };

  // Отправка формы
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Пожалуйста, введите название маршрута');
      return;
    }

    // Валидация координат всех точек
    if (!validateCoordinates()) {
      setError('Пожалуйста, исправьте ошибки в координатах точек');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // 1. Подготовка дополнительной информации
      const additionalInfoData = formData.additionalInfo
        .filter(info => info.title && info.title.trim() !== '')
        .map(info => ({
          title: info.title.trim(),
          description: info.description ? info.description.trim() : ''
        }));

      // 2. Подготовка данных точек (с парсингом координат)
      const pointsData = formData.points.map(point => {
        const parsed = parseCoordinates(point.coordinates);
        return {
          name: point.name || '',
          description: point.description || '',
          latitude: parsed ? parsed.latitude : 0,
          longitude: parsed ? parsed.longitude : 0
        };
      });

      // 3. Формируем объект маршрута
      const routeData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        additionalInfo: additionalInfoData,
        distance: parseFloat(formData.distance) || 0,
        time: parseInt(formData.time) || 0,
        steps: parseInt(formData.steps) || 0,
        points: pointsData
      };

      console.log('Отправка данных маршрута:', JSON.stringify(routeData, null, 2));

      // 4. Создание маршрута
      const createdRoute = await routeService.createRoute(routeData);
      console.log('Маршрут создан с ID:', createdRoute.id);

      // 5. Загрузка всех фотографий
      if (formData.mainPhoto || Object.keys(tempPointPhotos).length > 0) {
        await uploadAllPhotos(createdRoute.id, createdRoute.points);
        console.log('Все фотографии загружены');
      }

      alert('Маршрут успешно создан!');
      navigate('/admin/locations/routes');
      
    } catch (err) {
      console.error('Ошибка при создании маршрута:', err);
      setError(err.message || 'Произошла ошибка при создании маршрута');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-route-form">
      <h2 className="admin-route-form_title">Добавление маршрута</h2>

      {error && (
        <div className="admin-route-form_error">
          <span className="error-icon">⚠️</span> {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="admin-route-form_form">
        {/* Основная информация */}
        <div className="admin-route-form_section">
          <h3 className="admin-route-form_section-title">Основная информация</h3>
          
          <div className="admin-route-form_field">
            <label htmlFor="name">Название маршрута <span className="required">*</span></label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              disabled={submitting}
              placeholder="Введите название маршрута"
              autoFocus
            />
          </div>

          <div className="admin-route-form_field">
            <label htmlFor="description">Описание маршрута</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows="4"
              disabled={submitting}
              placeholder="Введите описание маршрута"
            />
          </div>
        </div>

        {/* Основное фото */}
        <div className="admin-route-form_section">
          <h3 className="admin-route-form_section-title">Основная фотография</h3>
          
          <div className="admin-route-form_main-photo">
            {!formData.mainPhotoPreview ? (
              <div className="main-photo-upload">
                <label className={`btn btn-secondary ${submitting ? 'disabled' : ''}`}>
                  📁 Выберите основную фотографию
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleMainPhotoSelect}
                    style={{ display: 'none' }}
                    disabled={submitting}
                  />
                </label>
                <p className="hint">Рекомендуемый размер: 1200x800px</p>
              </div>
            ) : (
              <div className="photo-item">
                <img src={formData.mainPhotoPreview} alt="Превью основной фотографии" />
                <div className="photo-actions">
                  <label className={`btn btn-secondary btn-small ${submitting ? 'disabled' : ''}`}>
                    📁 Заменить
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleMainPhotoSelect}
                      style={{ display: 'none' }}
                      disabled={submitting}
                    />
                  </label>
                  <button 
                    type="button"
                    onClick={removeMainPhoto}
                    className="photo-remove"
                    disabled={submitting}
                    title="Удалить"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Характеристики маршрута */}
        <div className="admin-route-form_section">
          <h3 className="admin-route-form_section-title">Характеристики маршрута</h3>
          
          <div className="admin-route-form_route-data">
            <div className="admin-route-form_field">
              <label htmlFor="distance">Протяженность (км)</label>
              <input
                type="number"
                id="distance"
                name="distance"
                value={formData.distance}
                onChange={(e) => handleRouteDataChange('distance', e.target.value)}
                step="0.1"
                disabled={calculating || submitting}
                placeholder="Рассчитывается автоматически"
              />
            </div>

            <div className="admin-route-form_field">
              <label htmlFor="time">Время в пути (минут)</label>
              <input
                type="number"
                id="time"
                name="time"
                value={formData.time}
                onChange={(e) => handleRouteDataChange('time', e.target.value)}
                disabled={calculating || submitting}
                placeholder="Рассчитывается автоматически"
              />
            </div>

            <div className="admin-route-form_field">
              <label htmlFor="steps">Количество шагов</label>
              <input
                type="number"
                id="steps"
                name="steps"
                value={formData.steps}
                onChange={(e) => handleRouteDataChange('steps', e.target.value)}
                disabled={calculating || submitting}
                placeholder="Рассчитывается автоматически"
              />
            </div>
          </div>

          {formData.points.length >= 2 && (
            <p className="admin-route-form_data-hint">
              * Характеристики автоматически пересчитываются при изменении точек маршрута
            </p>
          )}
        </div>

        {/* Дополнительная информация */}
        <div className="admin-route-form_section">
          <div className="admin-route-form_section-header">
            <h3 className="admin-route-form_section-title">Дополнительная информация</h3>
            <button 
              type="button"
              onClick={addAdditionalInfo}
              className="btn btn-secondary btn-small"
              disabled={submitting}
            >
              + Добавить блок
            </button>
          </div>

          {formData.additionalInfo.length === 0 && (
            <p className="empty-message">Нет блоков дополнительной информации</p>
          )}

          {formData.additionalInfo.map((info, index) => (
            <div key={index} className="info-block">
              <div className="info-block-header">
                <h4>Блок {index + 1}</h4>
                {formData.additionalInfo.length > 1 && (
                  <button 
                    type="button"
                    onClick={() => removeAdditionalInfo(index)}
                    className="btn-icon btn-remove"
                    disabled={submitting}
                  >
                    ✕
                  </button>
                )}
              </div>
              
              <div className="admin-route-form_field">
                <label>Заголовок</label>
                <input
                  type="text"
                  value={info.title}
                  onChange={(e) => handleAdditionalInfoChange(index, 'title', e.target.value)}
                  disabled={submitting}
                  placeholder="Введите заголовок"
                />
              </div>

              <div className="admin-route-form_field">
                <label>Описание</label>
                <textarea
                  value={info.description}
                  onChange={(e) => handleAdditionalInfoChange(index, 'description', e.target.value)}
                  disabled={submitting}
                  placeholder="Введите описание"
                  rows="3"
                />
              </div>
            </div>
          ))}
        </div>

        {/* Точки маршрута (без карты) */}
        <div className="admin-route-form_section">
          <div className="admin-route-form_section-header">
            <h3 className="admin-route-form_section-title">Точки маршрута</h3>
            <div className="section-buttons">
              <button 
                type="button"
                onClick={addPoint}
                className="btn btn-secondary btn-small"
                disabled={submitting}
              >
                + Добавить точку
              </button>
              {formData.points.length > 0 && (
                <button 
                  type="button"
                  onClick={clearAllPoints}
                  className="btn btn-secondary btn-small"
                  disabled={submitting}
                >
                  Очистить все точки
                </button>
              )}
            </div>
          </div>

          {/* Список точек */}
          {formData.points.length === 0 && (
            <p className="empty-message">Нет добавленных точек</p>
          )}

          {formData.points.map((point, pointIndex) => (
            <div key={pointIndex} className="point-block">
              <div className="point-block-header">
                <h5>Точка {pointIndex + 1}</h5>
                <div className="point-block-actions">
                  {pointIndex > 0 && (
                    <button
                      type="button"
                      onClick={() => movePointUp(pointIndex)}
                      className="btn-icon btn-move-up"
                      disabled={submitting}
                      title="Переместить вверх"
                    >
                      ↑
                    </button>
                  )}
                  {pointIndex < formData.points.length - 1 && (
                    <button
                      type="button"
                      onClick={() => movePointDown(pointIndex)}
                      className="btn-icon btn-move-down"
                      disabled={submitting}
                      title="Переместить вниз"
                    >
                      ↓
                    </button>
                  )}
                  {formData.points.length > 1 && (
                    <button 
                      type="button"
                      onClick={() => removePoint(pointIndex)}
                      className="btn-icon btn-remove"
                      disabled={submitting}
                      title="Удалить точку"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              <div className="admin-route-form_field">
                <label>Название точки</label>
                <input
                  type="text"
                  value={point.name}
                  onChange={(e) => updatePointInfo(pointIndex, 'name', e.target.value)}
                  disabled={submitting}
                  placeholder="Введите название точки"
                />
              </div>

              <div className="admin-route-form_field">
                <label>Описание точки</label>
                <textarea
                  value={point.description}
                  onChange={(e) => updatePointInfo(pointIndex, 'description', e.target.value)}
                  disabled={submitting}
                  placeholder="Введите описание точки"
                  rows="2"
                />
              </div>

              {/* Поле для координат */}
              <div className="admin-route-form_field">
                <label>Координаты</label>
                <input
                  type="text"
                  value={point.coordinates}
                  onChange={(e) => updatePointInfo(pointIndex, 'coordinates', e.target.value)}
                  disabled={submitting}
                  placeholder="55.751244, 37.618423"
                  className={coordinateErrors[pointIndex] ? 'error-input' : ''}
                />
                <p className="hint">Формат: широта, долгота (через запятую и пробел)</p>
                {coordinateErrors[pointIndex] && (
                  <div className="error-message">{coordinateErrors[pointIndex]}</div>
                )}
              </div>

              {/* Фотографии точки */}
              <div className="point-photos">
                <label>Фотографии точки</label>
                
                <div className="photo-upload">
                  <label className={`btn btn-secondary btn-small ${submitting ? 'disabled' : ''}`}>
                    📎 Добавить фото
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(e) => handlePointPhotoSelect(pointIndex, e.target.files)}
                      style={{ display: 'none' }}
                      disabled={submitting}
                    />
                  </label>
                </div>

                {tempPointPhotos[pointIndex] && tempPointPhotos[pointIndex].length > 0 && (
                  <div className="photo-grid">
                    {tempPointPhotos[pointIndex].map((photo, photoIndex) => (
                      <div key={photoIndex} className="photo-item">
                        <img 
                          src={URL.createObjectURL(photo)} 
                          alt={photo.name}
                        />
                        <button
                          type="button"
                          onClick={() => removePointTempPhoto(pointIndex, photoIndex)}
                          className="photo-remove"
                          disabled={submitting}
                          title="Удалить"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Кнопки действий */}
        <div className="admin-route-form_actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={submitting || calculating || !formData.name.trim()}
          >
            {submitting ? (
              <>
                <span className="btn-spinner"></span>
                Сохранение...
              </>
            ) : (
              'Сохранить маршрут'
            )}
          </button>
          <button 
            type="button" 
            onClick={() => navigate('/admin/locations/routes')} 
            className="btn btn-secondary"
            disabled={submitting}
          >
            Отмена
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminRouteAdd;